import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject
} from '@angular/core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { ChatExportDetailModalComponent } from './components/chat-export-detail-modal.component';
import { ChatExportFormComponent } from './components/chat-export-form.component';
import { ChatExportListComponent } from './components/chat-export-list.component';
import { ChatExportConfig, ChatExportStatus, ChatExportTask } from './models/chat-export.model';
import { ChatExportService } from './services/chat-export.service';

@Component({
  selector: 'servicenumber-chat-export',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    I18nPipe,
    TestIdDirective,
    ChatExportFormComponent,
    ChatExportListComponent,
    ChatExportDetailModalComponent
  ],
  templateUrl: './chat-export.component.html',
  styleUrls: ['./chat-export.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatExportComponent implements OnInit, OnChanges, OnDestroy {
  private readonly chatExportService = inject(ChatExportService);
  private readonly modalService = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  @Input() serviceNumberId: string = '';
  @Input() serviceNumberName: string = '';
  @Input() serviceNumberType: string = '';

  activeTab: 'new' | 'records' = 'new';
  loading: boolean = false;
  submitting: boolean = false;

  config: ChatExportConfig = {
    serviceNumberId: '',
    timeZone: 'Asia/Taipei',
    maxMonth: '',
    canManage: true
  };

  records: ChatExportTask[] = [];

  // Modals state
  detailModalVisible: boolean = false;
  selectedDetailTask: ChatExportTask | null = null;
  previewModalVisible: boolean = false;

  private pollSubscription?: Subscription;

  ngOnInit(): void {
    if (this.serviceNumberId) {
      this.loadConfigAndRecords();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serviceNumberId'] && !changes['serviceNumberId'].isFirstChange()) {
      this.stopPolling();
      this.loadConfigAndRecords();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  setActiveTab(tab: 'new' | 'records'): void {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  loadConfigAndRecords(): void {
    this.loading = true;
    this.chatExportService.getConfig(this.serviceNumberId).subscribe({
      next: cfg => {
        if (cfg) {
          this.config = {
            ...cfg,
            serviceNumberName: this.serviceNumberName || cfg.serviceNumberName
          };
        }
        this.fetchRecords();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  fetchRecords(isManualRefresh = false): void {
    this.chatExportService.getRecords(this.serviceNumberId).subscribe({
      next: list => {
        this.records = list || [];
        this.loading = false;
        if (isManualRefresh) {
          this.message.success(this.i18n.fanyi('servicenumber.chat-export.msg-refreshed'));
        }
        this.checkAndSchedulePolling();
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  handleSubmitExport(month: string): void {
    this.submitting = true;
    this.cdr.markForCheck();

    this.chatExportService.submitExport(this.serviceNumberId, month).subscribe({
      next: res => {
        this.submitting = false;
        this.message.success(this.i18n.fanyi('servicenumber.chat-export.msg-submit-success'));
        this.activeTab = 'records';
        this.fetchRecords();
      },
      error: err => {
        this.submitting = false;
        this.message.error(err?.msg || this.i18n.fanyi('servicenumber.chat-export.msg-submit-error'));
        this.cdr.markForCheck();
      }
    });
  }

  handleDownload(task: ChatExportTask): void {
    this.chatExportService.authorizeDownload(task.id).subscribe({
      next: grant => {
        if (grant && grant.downloadUrl) {
          this.message.info(this.i18n.fanyi('servicenumber.chat-export.msg-download-authorized'));
          window.open(grant.downloadUrl, '_blank', 'noopener,noreferrer');
        } else {
          this.message.error(this.i18n.fanyi('servicenumber.chat-export.msg-download-failed'));
        }
      },
      error: err => {
        this.message.error(err?.msg || this.i18n.fanyi('servicenumber.chat-export.msg-download-rejected'));
      }
    });
  }

  handleDelete(task: ChatExportTask): void {
    const isProcessing = task.status === ChatExportStatus.PROCESSING;
    const alertExtra = isProcessing
      ? `<br><br><span style='color:#1765ae'>${this.i18n.fanyi('servicenumber.chat-export.delete-confirm-extra-processing')}</span>`
      : '';

    this.modalService.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.chat-export.delete-confirm-title', { month: task.month }),
      nzContent: `${this.i18n.fanyi('servicenumber.chat-export.delete-confirm-content')}${alertExtra}`,
      nzOkText: this.i18n.fanyi('servicenumber.chat-export.delete-confirm-ok'),
      nzOkDanger: true,
      nzCancelText: this.i18n.fanyi('servicenumber.chat-export.delete-confirm-cancel'),
      nzOnOk: () => {
        task.isDeleting = true;
        task.cleanupError = false;
        this.cdr.markForCheck();

        this.chatExportService.deleteExport(task.id).subscribe({
          next: () => {
            this.message.success(this.i18n.fanyi('servicenumber.chat-export.msg-delete-initiated', { month: task.month }));
            this.fetchRecords();
          },
          error: err => {
            task.isDeleting = false;
            task.cleanupError = true;
            this.message.error(err?.msg || this.i18n.fanyi('servicenumber.chat-export.msg-delete-failed'));
            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  handleDetail(task: ChatExportTask): void {
    this.selectedDetailTask = task;
    this.detailModalVisible = true;
    this.cdr.markForCheck();
  }

  closeDetailModal(): void {
    this.detailModalVisible = false;
    this.cdr.markForCheck();
  }

  handlePreviewStyle(): void {
    this.previewModalVisible = true;
    this.cdr.markForCheck();
  }

  closePreviewModal(): void {
    this.previewModalVisible = false;
    this.cdr.markForCheck();
  }

  private checkAndSchedulePolling(): void {
    const hasActiveTasks = this.records.some(t => t.status === ChatExportStatus.PROCESSING || t.isDeleting);
    if (!hasActiveTasks) {
      this.stopPolling();
      return;
    }

    if (this.pollSubscription) {
      return;
    }

    // 當存在處理中或正在刪除的任務時，每 4 秒自動靜默輪詢最新狀態
    this.pollSubscription = timer(4000, 4000)
      .pipe(switchMap(() => this.chatExportService.getRecords(this.serviceNumberId)))
      .subscribe({
        next: list => {
          this.records = list || [];
          const stillActive = this.records.some(t => t.status === ChatExportStatus.PROCESSING || t.isDeleting);
          if (!stillActive) {
            this.stopPolling();
            // 任務完成後刷新配置以解鎖月份
            this.chatExportService.getConfig(this.serviceNumberId).subscribe(cfg => {
              if (cfg) {
                this.config = {
                  ...cfg,
                  serviceNumberName: this.serviceNumberName || cfg.serviceNumberName
                };
              }
              this.cdr.markForCheck();
            });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          // 靜默容錯，保持下一次輪詢
        }
      });
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }
}
